/**
 * Phase J.3.4 — Admin "Auto-charge attempts" list page.
 *
 * Lists every off-session ACH attempt fired by the auto-charge processor
 * with filters (status, customer, date range), invoice + customer
 * decoration, and a "Retry now" action for failed/abandoned rows.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { DashboardShell } from "@/components/DashboardShell";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
    Repeat, ChevronLeft, RefreshCcw, Filter, Play, AlertCircle, Loader2,
} from "lucide-react";

const STATUS_OPTIONS = [
    { value: "", label: "All" },
    { value: "pending", label: "Pending" },
    { value: "processing", label: "Processing" },
    { value: "succeeded", label: "Succeeded" },
    { value: "failed", label: "Failed" },
    { value: "abandoned", label: "Abandoned" },
];

const STATUS_PILL = {
    pending:    "bg-slate-100 text-slate-700 ring-slate-200",
    processing: "bg-amber-50 text-amber-900 ring-amber-200",
    succeeded:  "bg-emerald-50 text-emerald-900 ring-emerald-200",
    failed:     "bg-rose-50 text-rose-900 ring-rose-200",
    abandoned:  "bg-rose-100 text-rose-900 ring-rose-300",
};


function fmtMoney(cents) {
    if (cents == null) return "—";
    return `$${(cents / 100).toLocaleString("en-US", { minimumFractionDigits: 2 })}`;
}
function fmtDate(s) {
    if (!s) return "—";
    try { return new Date(s).toLocaleString(); } catch { return s; }
}


export default function AdminAutoChargeAttemptsPage() {
    const [sp, setSp] = useSearchParams();
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [retrying, setRetrying] = useState(null);

    const status = sp.get("status") || "";
    const customer_org_id = sp.get("customer_org_id") || "";

    const params = useMemo(() => {
        const p = { limit: 100 };
        if (status) p.status = status;
        if (customer_org_id) p.customer_org_id = customer_org_id;
        return p;
    }, [status, customer_org_id]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/auto-charge-attempts", { params });
            setItems(data?.items || []);
            setTotal(data?.total || 0);
            setError(null);
        } catch (e) {
            setError(formatApiError(e));
        } finally {
            setLoading(false);
        }
    }, [params]);

    useEffect(() => { load(); }, [load]);

    const handleRetry = async (id) => {
        setRetrying(id);
        try {
            await api.post(`/admin/auto-charge-attempts/${id}/retry-now`);
            toast.success("Retry initiated");
            await load();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setRetrying(null);
        }
    };

    const runRetriesNow = async () => {
        try {
            const { data } = await api.post("/admin/crons/run-auto-charge-retries");
            toast.success(
                `Retry tick: scanned ${data.scanned}, attempted ${data.attempted}, errors ${data.errors}`
            );
            await load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const setStatus = (v) => {
        const next = new URLSearchParams(sp);
        if (v) next.set("status", v); else next.delete("status");
        setSp(next);
    };

    return (
        <DashboardShell>
            <Link
                to="/admin"
                className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900 mb-3"
            >
                <ChevronLeft size={14} /> Dashboard
            </Link>
            <p className="text-xs font-medium tracking-[0.18em] uppercase text-blue-900 mb-3">
                Recurring billing
            </p>
            <div className="flex items-end justify-between gap-3 flex-wrap">
                <div>
                    <h1
                        data-testid="auto-charge-heading"
                        className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900"
                    >
                        Auto-charge attempts
                    </h1>
                    <p className="text-sm text-slate-600 mt-2 max-w-2xl">
                        Every off-session ACH charge fired by the recurring
                        billing cron. Use this to chase failed attempts and
                        spot abandoned schedules.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={load}
                        data-testid="auto-charge-refresh"
                        className="gap-1.5"
                    >
                        <RefreshCcw size={14} /> Refresh
                    </Button>
                    <Button
                        size="sm"
                        onClick={runRetriesNow}
                        data-testid="auto-charge-run-retries"
                        className="bg-blue-900 hover:bg-blue-800 gap-1.5"
                    >
                        <Play size={14} /> Run retry tick now
                    </Button>
                </div>
            </div>

            <Card className="mt-6">
                <CardContent className="p-4 space-y-3">
                    <div className="flex items-center gap-3 flex-wrap">
                        <span className="text-xs font-semibold tracking-wider uppercase text-slate-500 inline-flex items-center gap-1.5">
                            <Filter size={12} /> Filter
                        </span>
                        {STATUS_OPTIONS.map((opt) => (
                            <button
                                key={opt.value || "all"}
                                type="button"
                                data-testid={`auto-charge-filter-${opt.value || "all"}`}
                                onClick={() => setStatus(opt.value)}
                                className={
                                    "px-3 py-1.5 rounded-full text-xs font-medium ring-1 ring-inset transition-colors " +
                                    ((opt.value === status || (!opt.value && !status))
                                        ? "bg-slate-900 text-white ring-slate-900"
                                        : "bg-white text-slate-700 ring-slate-200 hover:bg-slate-50")
                                }
                            >
                                {opt.label}
                            </button>
                        ))}
                        <div className="flex items-center gap-2 ml-auto">
                            <Label htmlFor="ac-cust" className="text-xs text-slate-500">Customer org id</Label>
                            <Input
                                id="ac-cust"
                                value={customer_org_id}
                                onChange={(e) => {
                                    const next = new URLSearchParams(sp);
                                    if (e.target.value) next.set("customer_org_id", e.target.value);
                                    else next.delete("customer_org_id");
                                    setSp(next);
                                }}
                                placeholder="any"
                                className="w-48"
                                data-testid="auto-charge-filter-customer"
                            />
                        </div>
                    </div>
                </CardContent>
            </Card>

            <Card className="mt-4">
                <CardContent className="p-0">
                    {loading && (
                        <div className="p-6 text-sm text-slate-500 inline-flex items-center gap-2">
                            <Loader2 size={14} className="animate-spin" /> Loading attempts…
                        </div>
                    )}
                    {error && (
                        <div className="m-4 rounded-md bg-red-50 ring-1 ring-red-200 p-3 text-sm text-red-800 flex items-start gap-2">
                            <AlertCircle size={14} className="mt-0.5" />
                            <span>{error}</span>
                        </div>
                    )}
                    {!loading && !error && items.length === 0 && (
                        <div
                            data-testid="auto-charge-empty"
                            className="p-10 text-center"
                        >
                            <div className="mx-auto h-12 w-12 rounded-full bg-slate-50 border border-slate-200 flex items-center justify-center text-slate-400">
                                <Repeat size={20} />
                            </div>
                            <h3 className="mt-3 font-display text-base font-semibold text-slate-900">
                                No auto-charge attempts yet
                            </h3>
                            <p className="mt-1 max-w-sm mx-auto text-sm text-slate-600">
                                Attempts appear here as soon as a recurring schedule
                                with auto-charge enabled fires its first invoice.
                            </p>
                        </div>
                    )}
                    {!loading && !error && items.length > 0 && (
                        <table className="min-w-full divide-y divide-slate-200" data-testid="auto-charge-table">
                            <thead className="bg-slate-50">
                                <tr className="text-xs uppercase tracking-wider text-slate-500">
                                    <th className="px-4 py-3 text-left">Invoice</th>
                                    <th className="px-4 py-3 text-left">Customer</th>
                                    <th className="px-4 py-3 text-left">Amount</th>
                                    <th className="px-4 py-3 text-left">Attempt</th>
                                    <th className="px-4 py-3 text-left">Status</th>
                                    <th className="px-4 py-3 text-left">Scheduled</th>
                                    <th className="px-4 py-3 text-left">Attempted</th>
                                    <th className="px-4 py-3 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-slate-200">
                                {items.map((row) => {
                                    const pill = STATUS_PILL[row.status] || STATUS_PILL.pending;
                                    return (
                                        <tr
                                            key={row.id}
                                            data-testid={`auto-charge-row-${row.id}`}
                                            className="text-sm hover:bg-slate-50"
                                        >
                                            <td className="px-4 py-3">
                                                {row.invoice?.id ? (
                                                    <Link
                                                        to={`/admin/invoices/${row.invoice.id}`}
                                                        className="text-blue-900 font-medium hover:underline"
                                                    >
                                                        {row.invoice?.number || row.invoice.id.slice(0, 8)}
                                                    </Link>
                                                ) : "—"}
                                            </td>
                                            <td className="px-4 py-3 text-slate-700">
                                                {row.customer?.company_name || "—"}
                                            </td>
                                            <td className="px-4 py-3 font-mono text-slate-900">
                                                {fmtMoney(row.amount_cents ?? row.invoice?.total_cents)}
                                            </td>
                                            <td className="px-4 py-3 text-slate-700">
                                                #{row.attempt_number}
                                            </td>
                                            <td className="px-4 py-3">
                                                <span className={"inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ring-inset " + pill}>
                                                    {row.status}
                                                </span>
                                                {row.error_message && (
                                                    <p className="text-[11px] text-rose-700 mt-1 max-w-xs truncate" title={row.error_message}>
                                                        {row.error_message}
                                                    </p>
                                                )}
                                            </td>
                                            <td className="px-4 py-3 font-mono text-xs text-slate-600">
                                                {row.scheduled_for || "—"}
                                            </td>
                                            <td className="px-4 py-3 text-xs text-slate-600">
                                                {fmtDate(row.attempted_at)}
                                            </td>
                                            <td className="px-4 py-3 text-right">
                                                {(row.status === "failed" || row.status === "abandoned") && (
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        onClick={() => handleRetry(row.id)}
                                                        disabled={retrying === row.id}
                                                        data-testid={`auto-charge-retry-${row.id}`}
                                                        className="gap-1.5"
                                                    >
                                                        {retrying === row.id ? (
                                                            <Loader2 size={12} className="animate-spin" />
                                                        ) : (
                                                            <Play size={12} />
                                                        )}
                                                        Retry now
                                                    </Button>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                    <div className="px-4 py-2.5 border-t border-slate-100 text-xs text-slate-500">
                        Showing {items.length} of {total} attempts.
                    </div>
                </CardContent>
            </Card>
        </DashboardShell>
    );
}
